import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Hero from './components/Hero';
import loadable from '@loadable/component';
import ParticleBackground from './components/ParticleBackground';

// Глобальная функция для исправления скролла
const fixScrollbars = () => {
  // Находим все элементы, которые могут иметь скролл
  const elementsWithScroll = document.querySelectorAll('p, h1, h2, h3, h4, h5, h6, span, img, a, button, div');
  
  // Удаляем скролл для каждого элемента
  elementsWithScroll.forEach(el => {
    if (!el.classList.contains('overflow-y-auto') && 
        !el.classList.contains('overflow-y-scroll') && 
        !el.classList.contains('overflow-auto')) {
      el.style.overflow = 'visible';
      el.style.overflowX = 'visible';
      el.style.overflowY = 'visible';
    }
  });
  
  // Принудительно устанавливаем overflow: hidden для body и html
  document.body.style.overflowX = 'hidden';
  document.documentElement.style.overflowX = 'hidden';
  
  // Исправляем потенциальные проблемы с контейнерами
  const containers = document.querySelectorAll('section, main, article, header, footer');
  containers.forEach(el => {
    el.style.maxWidth = '100%';
    el.style.overflowX = 'hidden';
  });
  
  // Особый случай для навигации
  const navContainer = document.querySelector('.fixed.right-8');
  if (navContainer) {
    navContainer.style.overflow = 'visible';
    navContainer.style.maxWidth = 'none';
    navContainer.style.maxHeight = 'none';
    
    // Применяем к дочерним элементам
    const navElements = navContainer.querySelectorAll('*');
    navElements.forEach(el => {
      el.style.overflow = 'visible';
    });
  }
  
  // Особый случай для абсолютно позиционированных элементов
  const absoluteElements = document.querySelectorAll('.absolute');
  absoluteElements.forEach(el => {
    if (el.classList.contains('whitespace-nowrap') || 
        el.parentElement.classList.contains('group') ||
        el.classList.contains('left-1/2')) {
      el.style.overflow = 'visible';
      el.style.maxWidth = 'none';
      el.style.pointerEvents = 'none';
    }
  });
};

// Ленивая загрузка компонентов
const About = loadable(() => import('./components/About'), {
  fallback: <div className="min-h-screen flex items-center justify-center">
    <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
  </div>
});

const Skills = loadable(() => import('./components/Skills'), {
  fallback: <div className="min-h-screen flex items-center justify-center">
    <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
  </div>
});

const Projects = loadable(() => import('./components/Projects'), {
  fallback: <div className="min-h-screen flex items-center justify-center">
    <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
  </div>
});

const GitHubRepos = loadable(() => import('./components/GitHubRepos'), {
  fallback: <div className="min-h-screen flex items-center justify-center">
    <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
  </div>
});

const Gaming = loadable(() => import('./components/Gaming'), {
  fallback: <div className="min-h-screen flex items-center justify-center">
    <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
  </div>
});

const Contact = loadable(() => import('./components/Contact'), {
  fallback: <div className="min-h-screen flex items-center justify-center">
    <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
  </div>
});

function App() {
  const [loading, setLoading] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('hero');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Создаем рефы для отслеживания видимости секций
  const sectionRefs = useRef({});

  // Имитация загрузки сайта
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
      // Исправляем скролл после загрузки
      setTimeout(fixScrollbars, 100);
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);

  // Отслеживание прокрутки для прогресс-бара
  useEffect(() => {
    const handleScrollProgress = () => {
      const totalHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);
      // Исправляем скролл при прокрутке
      fixScrollbars();
    };
    
    window.addEventListener('scroll', handleScrollProgress);
    return () => window.removeEventListener('scroll', handleScrollProgress);
  }, []);

  // Используем IntersectionObserver для определения активной секции
  useEffect(() => {
    const sections = ['hero', 'about', 'skills', 'projects', 'github-repos', 'gaming', 'contact'];
    const currentActiveSection = activeSection; // Копируем текущее значение для использования в cleanup
    
    // Функция для создания IntersectionObserver
    const createObserver = () => {
      // Опции для наблюдателя: мы хотим знать, когда секция пересекает центр экрана
      const options = {
        root: null, // viewport
        rootMargin: '0px',
        threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9] // наблюдаем разные пороги пересечения
      };
      
      // Создаем новый наблюдатель
      const observer = new IntersectionObserver((entries) => {
        // Обрабатываем каждую запись
        entries.forEach(entry => {
          // Добавляем/удаляем классы в зависимости от видимости
          if (entry.isIntersecting) {
            entry.target.classList.add('intersection-visible');
            entry.target.classList.remove('intersection-hidden');
          } else {
            entry.target.classList.add('intersection-hidden');
            entry.target.classList.remove('intersection-visible');
          }
        });
        
        // Сортируем записи по проценту пересечения (видимости)
        const visibleSections = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        
        // Если есть видимые секции, устанавливаем наиболее видимую как активную
        if (visibleSections.length > 0) {
          const mostVisibleSection = visibleSections[0].target.id;
          if (mostVisibleSection !== activeSection) {
            setActiveSection(mostVisibleSection);
            
            // Запускаем событие изменения активной секции для других скриптов
            const event = new CustomEvent('activeSectionChanged', { 
              detail: { section: mostVisibleSection } 
            });
            window.dispatchEvent(event);
          }
        }
      }, options);
      
      return observer;
    };
    
    // Создаем наблюдатель и регистрируем все секции
    const observer = createObserver();
    const currentRefs = {}; // Создаем локальную копию рефов
    
    // Получаем все элементы секций и начинаем наблюдение
    sections.forEach(sectionId => {
      const element = document.getElementById(sectionId);
      if (element) {
        // Инициализируем классы видимости
        if (sectionId === currentActiveSection) {
          element.classList.add('intersection-visible');
        } else {
          element.classList.add('intersection-hidden');
        }
        
        currentRefs[sectionId] = element; // Сохраняем в локальную копию
        sectionRefs.current[sectionId] = element;
        observer.observe(element);
      }
    });
    
    // Прекращаем наблюдение при размонтировании
    return () => {
      sections.forEach(sectionId => {
        const element = currentRefs[sectionId]; // Используем локальную копию
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, [activeSection]); // Добавляем activeSection в зависимости

  // Плавная прокрутка к секциям
  const smoothScroll = (e, target) => {
    e.preventDefault();
    const element = document.querySelector(target);
    if (element) {
      const targetId = target.substring(1);
      
      // Программно устанавливаем активную секцию
      setActiveSection(targetId);
      
      // Прокручиваем к элементу
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      
      // Закрываем мобильное меню
      setMobileMenuOpen(false);
      
      // Исправляем скролл после прокрутки
      setTimeout(fixScrollbars, 300);
    }
  };

  const navItems = [
    { id: 'hero', label: 'Главная' },
    { id: 'about', label: 'Обо мне' },
    { id: 'skills', label: 'Навыки' },
    { id: 'projects', label: 'Проекты' },
    { id: 'github-repos', label: 'GitHub' },
    { id: 'gaming', label: 'Игры' },
    { id: 'contact', label: 'Контакты' }
  ];

  return (
    <>
      <AnimatePresence>
        {loading ? (
          <motion.div 
            key="loader"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 bg-primary flex items-center justify-center z-50"
          >
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
                rotate: [0, 180, 360] 
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut" 
              }}
              className="w-20 h-20 relative"
            >
              <div className="absolute inset-0 rounded-full border-t-4 border-accent opacity-75 animate-spin"></div>
              <div className="absolute inset-0 rounded-full border-r-4 border-accent-secondary opacity-75 animate-spin animation-delay-500"></div>
            </motion.div>
            <motion.h1 
              className="absolute mt-32 text-2xl font-bold bg-gradient-to-r from-accent to-accent-secondary text-transparent bg-clip-text"
              animate={{ opacity: [0, 1, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Sysrootix
            </motion.h1>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <div className="min-h-screen bg-primary relative w-full">
        {/* Прогресс-бар прокрутки */}
        <div 
          className="fixed top-0 left-0 h-1 bg-gradient-to-r from-accent via-accent-secondary to-accent z-50"
          style={{ width: `${scrollProgress}%` }}
        ></div>
        
        {/* Фон с частицами */}
        <ParticleBackground />
        
        {/* Мобильная навигация */}
        <div className="fixed top-4 right-4 z-50 lg:hidden">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 bg-secondary/80 backdrop-blur-sm rounded-lg border border-white/10 hover:border-accent/30 transition-all"
            aria-label={mobileMenuOpen ? "Закрыть меню" : "Открыть меню"}
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            ) : (
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
              </svg>
            )}
          </button>
        </div>
        
        {/* Мобильное меню */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="fixed top-16 right-4 z-50 lg:hidden bg-secondary/90 backdrop-blur-md border border-white/10 rounded-xl p-4 shadow-lg"
            >
              <ul className="flex flex-col gap-3">
                {navItems.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      onClick={(e) => smoothScroll(e, `#${item.id}`)}
                      className={`block px-4 py-2 rounded-lg transition-all duration-200 ${activeSection === item.id ? 'bg-accent/20 text-accent' : 'hover:bg-white/5'}`}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Навигационные точки (десктоп) */}
        <div className="fixed right-8 top-1/2 transform -translate-y-1/2 z-40 hidden lg:block">
          <div className="flex flex-col gap-6 relative overflow-visible">
            {/* Линия связи между точками */}
            <div className="absolute left-1/2 top-0 w-px h-full bg-secondary/30 -z-10 transform -translate-x-1/2"></div>
            
            {navItems.map((item) => (
              <div key={item.id} className="relative">
                {/* Индикатор активности (отображается только для активной секции) */}
                {activeSection === item.id && (
                  <div 
                    className="absolute left-1/2 top-1/2 w-5 h-5 rounded-full bg-accent shadow-neon -translate-x-1/2 -translate-y-1/2 z-20"
                  >
                    {/* Внутренний круг для эффекта пульсации */}
                    <div className="absolute inset-0 rounded-full bg-accent-secondary/70 animate-pulse"></div>
                    {/* Внешний круг для эффекта свечения */}
                    <div className="absolute -inset-2 rounded-full bg-accent/20 animate-ping opacity-70"></div>
                  </div>
                )}
                
                {/* Навигационная точка (всегда видна) */}
                <a 
                  href={`#${item.id}`}
                  onClick={(e) => smoothScroll(e, `#${item.id}`)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 group relative z-10 block
                    ${activeSection === item.id 
                      ? 'opacity-0' /* Скрываем точку, когда активен индикатор */
                      : 'bg-secondary hover:bg-accent hover:scale-110'}
                  `}
                  aria-label={`Перейти к секции ${item.label}`}
                >                
                  <span className={`absolute right-full mr-6 top-1/2 transform -translate-y-1/2 whitespace-nowrap 
                    transition-all duration-300 text-sm font-medium px-2 py-1 rounded pointer-events-none
                    ${activeSection === item.id 
                      ? 'opacity-100 bg-secondary/60 text-accent border-l-2 border-accent' 
                      : 'opacity-0 group-hover:opacity-80 group-hover:mr-5'}
                  `}>
                    {item.label}
                  </span>
                </a>
              </div>
            ))}
          </div>
          
          {/* Вертикальный индикатор справа */}
          <div className="absolute -right-4 top-0 bottom-0 w-1 rounded-full overflow-hidden">
            <div 
              className="bg-gradient-to-b from-accent via-accent-secondary to-accent h-[14%] transition-all duration-300"
              style={{
                transform: `translateY(${navItems.findIndex(item => item.id === activeSection) * 100}%)`
              }}
            ></div>
          </div>
        </div>
        
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <section id="hero" className="w-full">
            <Hero />
          </section>
          
          <div className="relative">
            {/* Декоративные разделители между секциями */}
            <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-primary to-transparent z-10 pointer-events-none"></div>
            
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
              <section id="about">
                <About />
              </section>
              
              {/* Декоративный разделитель */}
              <div className="h-24 w-full relative">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-accent-secondary to-transparent"></div>
                </div>
              </div>
              
              <section id="skills">
                <Skills />
              </section>
              
              {/* Декоративный разделитель */}
              <div className="h-24 w-full relative">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-accent to-transparent"></div>
                </div>
              </div>
              
              <section id="projects">
                <Projects />
              </section>
              
              {/* Декоративный разделитель */}
              <div className="h-24 w-full relative">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-glow-purple to-transparent"></div>
                </div>
              </div>
              
              <section id="github-repos">
                <GitHubRepos />
              </section>
              
              <section id="gaming">
                <Gaming />
              </section>
              
              {/* Декоративный разделитель */}
              <div className="h-24 w-full relative">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-accent-secondary to-transparent"></div>
                </div>
              </div>
              
              <section id="contact">
                <Contact />
              </section>
              
              <footer className="py-8 text-center text-text-secondary text-sm">
                <p>&copy; {new Date().getFullYear()} Sysrootix. Все права защищены.</p>
                <div className="flex justify-center gap-6 mt-4">
                  <a href="https://github.com/sysrootix" target="_blank" rel="noopener noreferrer" className="text-text-secondary hover:text-accent transition-colors duration-300">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                    </svg>
                  </a>
                  <a href="https://t.me/sysrootix" target="_blank" rel="noopener noreferrer" className="text-text-secondary hover:text-accent transition-colors duration-300">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5.38 7.38c-.22 2.13-1.19 7.24-1.68 9.61-.2.97-.59 1.31-1.07 1.33-.95.05-1.63-.46-2.45-1.2-1.41-1.26-2.21-2.03-3.57-3.25-.98-.75-.35-1.17.21-1.84.15-.17 2.62-2.5 2.68-2.71.01-.04.01-.21-.08-.3-.09-.09-.23-.12-.33-.09-.14.04-2.46 1.47-3.69 2.21-.63.41-1.11.5-1.59.5-.53-.01-1.57-.28-2.37-.51-.96-.28-1.67-.44-1.6-1.1.04-.37.29-.67 1.24-.99 4.69-1.96 7.77-3.21 9.26-3.75 4.42-1.62 5.36-.37 4.99 1.09z"/>
                    </svg>
                  </a>
                </div>
              </footer>
            </div>
            
            {/* Декоративные разделители в конце страницы */}
            <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-primary to-transparent z-10 pointer-events-none"></div>
          </div>
        </motion.div>
      </div>
    </>
  );
}

export default App;
